/**
 * Watson Construction phase codes (cost codes) that foremen tag daily-report lines with.
 * Seeded from Watson's "PHASE CODE REFERENCE" sheet; the office can edit the list in the app.
 * A code is the base number ("7620"); the cost type is the suffix: .1 labor, .2 material,
 * .4 subcontractor, .5 rental, .6 purchased labor.
 */
export interface PhaseCode { code: string; name: string; group: string }

export const PHASE_COST_TYPES = { labor: ".1", material: ".2", subcontractor: ".4", rental: ".5", purchasedLabor: ".6" } as const;
export type PhaseCostType = keyof typeof PHASE_COST_TYPES;

const G = (group: string, rows: Array<[string, string]>): PhaseCode[] => rows.map(([code, name]) => ({ code, name, group }));

export const DEFAULT_PHASE_CODES: PhaseCode[] = [
  ...G("Sitework", [
    ["1100", "Permits"], ["1101", "Clean Up"], ["1110", "Survey & Layout"], ["1120", "Testing"],
    ["1125", "Tree Grates"], ["1130", "Tree Barricades"], ["1140", "Erosion Control"], ["1145", "Mobilization"], ["1150", "MOT"],
    ["1160", "Dewatering"], ["1210", "Clearing"], ["1215", "Stripping"], ["1220", "Demolition - Above Ground"], ["1221", "Demolition - Underground"],
    ["1224", "Dumpster Rental"], ["1225", "Porta-Potty"], ["1226", "Job Trailer Rental"], ["1227", "Safety"], ["1228", "Job Utilities (Water & Sewer)"],
    ["1440", "Underdrain - Labor"], ["1630", "Colored Concrete"], ["1635", "Architectural Concrete"], ["1640", "Concrete Walls"], ["1645", "Special Concrete Finish"],
    ["1650", "Concrete (Miscellaneous)"], ["1655", "Concrete Paving"], ["1657", "Pressure Grouting"], ["1660", "Sidewalks"], ["1665", "ADA Surface"], ["1670", "Gravel Area"],
    ["1810", "Seed, Mulch, Sod"], ["1820", "Mock-ups"], ["1826", "Temporary Fence"], ["1827", "Permanent Fence"], ["1829", "Segmented Block Wall"],
    ["1830", "Fencing / Wall"], ["1835", "Bollards"], ["1845", "Crossings"], ["1910", "Electrical"], ["1915", "Gas Piping"],
  ]),
  ...G("Earthwork", [
    ["2160", "Dewatering"], ["2310", "Cut-Dispose"], ["2315", "Cut-Fill (Balance)"], ["2320", "Import (Haul)"], ["2325", "Proof Rolling & Compact"],
    ["2330", "Stockpile (Haul)"], ["2340", "Undercut - Labor"], ["2350", "Grading"], ["2355", "Pond Liner"],
  ]),
  ...G("Storm", [["3410", "L&E, Pipe & Fittings"], ["3420", "Structures"], ["3425", "TV Inspection"], ["3430", "Rip Rap"]]),
  ...G("Sanitary", [["4410", "L&E, Pipe & Fittings"], ["4420", "Structures"]]),
  ...G("Water", [["5120", "Water Certification"], ["5410", "L&E, Pipe & Fittings"], ["5420", "Structures"]]),
  ...G("Fire", [["6410", "L&E, Fittings"], ["6420", "Structures"]]),
  ...G("Road Work", [
    ["7580", "Subgrade"], ["7585", "Geogrid"], ["7610", "Limerock Base"], ["7615", "Milling Asphalt"], ["7620", "Asphalt"], ["7621", "Bridge Work"],
    ["7630", "Curbs"], ["7640", "Concrete Paver"], ["7650", "57 Stone"], ["7660", "Pavement Mark/Signs"], ["7680", "Signalization"],
  ]),
];

/** "7620" + labor -> "7620.1". A code that already has a suffix is kept as is. */
export function phaseCodeFor(code: string, type?: PhaseCostType): string {
  if (!code) return "";
  if (!type || code.includes(".")) return code;
  return code + PHASE_COST_TYPES[type];
}

export function phaseLabel(code: string | undefined, list: PhaseCode[], type?: PhaseCostType): string {
  if (!code) return "";
  const hit = list.find((p) => p.code === code);
  return `${phaseCodeFor(code, type)}${hit ? ` ${hit.name}` : ""}`;
}

/** Cleans an office-edited list: trims, drops blanks and duplicate codes, keeps order. */
export function normalizePhaseCodes(input: unknown): PhaseCode[] {
  if (!Array.isArray(input)) throw new Error("Send a list of phase codes.");
  const seen = new Set<string>(), out: PhaseCode[] = [];
  for (const x of input.slice(0, 1000)) {
    if (!x || typeof x !== "object") continue;
    const r = x as Record<string, unknown>;
    const code = typeof r.code === "string" ? r.code.trim().slice(0, 20) : typeof r.code === "number" ? String(r.code) : "";
    const name = typeof r.name === "string" ? r.name.trim().slice(0, 120) : "";
    if (!code || !name || seen.has(code)) continue;
    seen.add(code);
    out.push({ code, name, group: typeof r.group === "string" && r.group.trim() ? r.group.trim().slice(0, 60) : "Other" });
  }
  if (!out.length) throw new Error("The phase list needs at least one code with a name.");
  return out;
}

/** "code, name, group" per line (office editor). */
export function parsePhaseCodeText(text: string): PhaseCode[] {
  return normalizePhaseCodes(text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
    const parts = l.split(/\s*[,\t]\s*/);
    return { code: parts[0], name: parts.slice(1, parts.length > 2 ? -1 : undefined).join(", "), group: parts.length > 2 ? parts.at(-1) : "" };
  }));
}
export const phaseCodeText = (list: PhaseCode[]) => list.map((p) => `${p.code}, ${p.name}, ${p.group}`).join("\n");
